import { useCallback, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useAnchoredMenu } from '../../hooks/useAnchoredMenu';
import { WorkspaceFolderPicker } from '../../pages/WorkspacePage/WorkspaceFolderPicker';
import { saveChatAttachmentToWorkspace } from '../../api/chat-attachment-save';
import { layerAlert } from '../LayerPopup/LayerPopup';
import './AttachmentSaveMenu.css';

/**
 * 첨부를 어디에 둘지 고르는 버튼 (chat-attachment-save — 2026-08-26).
 *
 * 대화에서 첨부를 내려받는 자리는 **넷**이다 — 파일 카드 · 그림 · 미디어 패널 목록 ·
 * 큰 그림 보기. 한 곳에만 갈림길이 생기면 "첨부를 눌렀는데 워크스페이스가 안 나온다" 가 되므로
 * 버튼을 통째로 빼서 넷이 함께 쓴다.
 *
 * ⚠ **메뉴는 body 로 띄운다.** 처음엔 버튼 옆에 `position:absolute` 로 뒀는데, 대화 목록이
 *   `overflow-y:auto` 라 메뉴가 **잘려서 안 보였다**. 그림처럼 화면 가장자리에 붙은 첨부는
 *   메뉴가 창 밖으로 나가기도 했다. 그래서 버튼 좌표를 재서 `position:fixed` 로 띄우고,
 *   자리가 모자라면 위로·안쪽으로 뒤집는다.
 *
 * 내 PC 로 내려받는 방법은 자리마다 조금씩 다르다(파일명·disposition). 그 부분은 호출부가
 * 그대로 넘겨 준다 — 기존 다운로드 경로를 한 줄도 바꾸지 않으려는 것이다.
 *
 * ## 어디에 담는가 (2026-09-02 확장)
 *
 * 처음에는 `roomId` 만 받았다 — 「대화 밖에서는 담을 방이 없다」는 전제였다. 그런데 메일에도
 * 같은 갈림길이 필요해졌다(사용자 요구). 담는 **권한 판정이 도메인마다 다르므로**(채팅은 방
 * 멤버십, 메일은 발신자/수신자) 창구도 도메인마다 다르다.
 *
 * 그래서 담는 방법을 **호출부가 넘긴다**: `onSaveToWorkspace(parentId)`.
 *   · 메일  — `saveMailAttachmentToWorkspace(mailId, fileId, …)`
 *   · 채팅  — 안 넘기면 `roomId` 로 종전 경로를 그대로 쓴다(호출부 넷 무수정)
 * 둘 다 없으면(캘린더 등) 갈림길 없이 곧장 내려받는다 — 지금까지와 같다.
 */

/** 메뉴 상자 크기 — CSS 와 함께 고친다. */
const MENU_SIZE = { width: 248, height: 150 };

export function AttachmentSaveMenu({
  roomId, fileId, onDownload, onSaveToWorkspace, className, buttonLabel, children,
}: {
  roomId?: string;
  fileId: string;
  /** '내 PC 에 저장' — 호출부의 기존 다운로드 동작을 그대로 받는다. */
  onDownload: () => void | Promise<void>;
  /**
   * '워크스페이스에 저장' — 도메인마다 권한 판정이 달라 담는 창구도 다르다(2026-09-02).
   * 안 넘기면 `roomId` 로 채팅 경로를 쓴다. 둘 다 없으면 갈림길 자체가 안 생긴다.
   */
  onSaveToWorkspace?: (parentId: string | null) => Promise<unknown>;
  className?: string;
  buttonLabel: string;
  children: ReactNode;
}) {
  const { anchorRef, pos, open, toggle, close } = useAnchoredMenu<HTMLButtonElement>(MENU_SIZE);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const saveToWorkspace = useCallback(async (parentId: string | null) => {
    if (!onSaveToWorkspace && !roomId) return;
    setBusy(true);
    try {
      if (onSaveToWorkspace) await onSaveToWorkspace(parentId);
      else await saveChatAttachmentToWorkspace(roomId!, fileId, { parentId });
      setPickerOpen(false);
      await layerAlert('워크스페이스에 담았습니다.');
    } catch (err: unknown) {
      const res = (err as { response?: { status?: number; data?: { message?: string } } })?.response;
      setPickerOpen(false);
      await layerAlert(res?.status === 404
        ? '이 서버는 아직 워크스페이스 담기를 지원하지 않습니다.'
        : res?.data?.message ?? '담지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }, [roomId, fileId, onSaveToWorkspace]);

  // 담을 곳이 없으면 지금까지처럼 곧장 내려받는다(무회귀).
  if (!onSaveToWorkspace && !roomId) {
    return (
      <button
        type="button"
        className={className}
        onClick={() => void onDownload()}
        disabled={busy}
        aria-label={buttonLabel}
      >
        {children}
      </button>
    );
  }

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={className}
        onClick={toggle}
        disabled={busy}
        aria-label={buttonLabel}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {children}
      </button>
      {pos && createPortal(
        <>
          {/* 바깥을 누르면 닫힌다 — 메뉴보다 뒤에 깔아 클릭을 먼저 받는다. */}
          <div className="chat-attachment-dl-veil" role="presentation" onMouseDown={close} />
          <div
            className="chat-attachment-dl-menu"
            role="menu"
            aria-label="저장 위치"
            style={{ top: pos.top, left: pos.left }}
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => { close(); void onDownload(); }}
            >
              <b>내 PC 에 저장</b>
              <span>다운로드 폴더로 내려받습니다</span>
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => { close(); setPickerOpen(true); }}
            >
              <b>워크스페이스에 저장</b>
              <span>폴더를 고른 뒤 담습니다</span>
            </button>
            <p className="chat-attachment-dl-note">
              워크스페이스에 담으면 내 보관 용량에서 차감됩니다.
            </p>
          </div>
        </>,
        document.body,
      )}
      {pickerOpen && (
        // 폴더 고르기는 워크스페이스의 것을 그대로 쓴다 — 목적지를 고르는 방식이 같다.
        <WorkspaceFolderPicker
          moving={[]}
          mode="copy"
          onClose={() => setPickerOpen(false)}
          onPick={(parentId) => saveToWorkspace(parentId)}
        />
      )}
    </>
  );
}
